import clsx from "clsx";
import { pick } from "lodash";
import {
  Card,
  KeyValueList,
  RenderWhen,
  ThreatStatusLabel
} from "../../../../../../components";
import {
  ClassificationDecisiveReason,
  SandboxAnalysis
} from "../../../../@types";
import { CardSubtitle } from "../../components";
import IntegrationsKey from "./IntegrationsKey";
import useParanoidMode from "../../../../../paranoid-mode/hooks/useParanoidMode";

import { SECTIONS } from "../../../../consts";
import {
  useGetSampleAnalysisStatus,
  useLocalSampleSummaryParams
} from "../../../../hooks";
import { getIsNotConfigured } from "../../utils";
import styles from "../analysis-card.module.scss";
import IntegrationsValue from "./IntegrationsValue";
import Subtitle from "./Subtitle";

import { enums } from "../../../../../../shim/jsbridge";

type Props = {
  data?: SandboxAnalysis;
  reason?: ClassificationDecisiveReason;
  onClick: (section: string) => void;
};

const getDecisiveClassification = (
  data?: SandboxAnalysis,
  reason?: ClassificationDecisiveReason
) => {
  if (!data || !reason) return "unknown";
  if (reason === "joe-sandbox")
    return data?.joe?.classification?.toLowerCase() ?? "unknown";
  return "unkown";
};

const Integrations = ({ data, reason, onClick }: Props) => {
  const { hash } = useLocalSampleSummaryParams();
  const { data: { dynamic_analysis: analysisStatus = undefined } = {} } =
    useGetSampleAnalysisStatus(hash);

  const { paranoidModeConfig } = useParanoidMode();
  const { cape, cuckoo, joe, fireeye, vmray, cisco_sma: ciscoSMA } = data ?? {};
  const sandboxValues = Object.values(data ?? {});

  const showNoAnalysis = !sandboxValues.find(item =>
    Boolean(item?.classification)
  );

  const enabledCount = Object.values(
    pick(analysisStatus, [
      "cuckoo",
      "cape",
      "joe",
      "fireeye",
      "vmray_tcbase",
      "cisco_secure_malware_analytics"
    ])
  ).filter(item => !getIsNotConfigured(item?.additional_information)).length;

  const impactFinalClassification = [enums.paranoidModeComponents.JOE].some(
    integration => paranoidModeConfig?.included?.includes(integration)
  );

  const decisive = ["joe-sandbox"].includes(reason ?? "");
  const decisiveClassification = getDecisiveClassification(data, reason);

  return (
    <Card
      className={clsx(
        decisive && styles[`decisive_reason_${decisiveClassification}`]
      )}
      disabled={!enabledCount && showNoAnalysis}
      icon="integrations-a"
      title="Integrations"
      subtitle={
        <CardSubtitle
          hasData
          impactsFinalClassification={impactFinalClassification}
        >
          <Subtitle
            data={sandboxValues}
            decisive={decisive}
            classification={decisiveClassification}
          />
        </CardSubtitle>
      }
      data-testid="report_summary_integrations"
    >
      <KeyValueList
        className={styles.analysis_card_content_wrapper}
        data-testid="report_summary_integrations_list"
      >
        <RenderWhen
          condition={Boolean(
            cape ||
              !getIsNotConfigured(analysisStatus?.cape.additional_information)
          )}
        >
          <KeyValueList.Item
            className={clsx(
              styles.list_item_integrations,
              styles.space_between,
              cape?.classification && styles.active
            )}
            keyClassName="font-normal"
            label={<IntegrationsKey icon="cape" title="CAPE" />}
            value={
              <IntegrationsValue
                integration="cape"
                hasResult={Boolean(cape?.classification)}
                status={
                  analysisStatus?.cape.display_status ||
                  analysisStatus?.cape.additional_information
                }
              >
                <ThreatStatusLabel
                  className={styles.threat_status}
                  threatStatus={cape?.classification?.toLowerCase()}
                  labelText={`${cape?.score}/10`}
                />
              </IntegrationsValue>
            }
            onClick={event => {
              event.stopPropagation();
              if (!cape?.classification) return;
              onClick(SECTIONS.DYNAMIC_ANALYSIS.CAPE);
            }}
            data-testid="report_summary_integrations_cape"
          />
        </RenderWhen>
        <RenderWhen
          condition={Boolean(
            cuckoo ||
              !getIsNotConfigured(analysisStatus?.cuckoo.additional_information)
          )}
        >
          <KeyValueList.Item
            className={clsx(
              styles.list_item_integrations,
              styles.space_between,
              cuckoo?.classification && styles.active
            )}
            keyClassName="font-normal"
            label={<IntegrationsKey icon="cuckoo" title="Cuckoo" />}
            value={
              <IntegrationsValue
                integration="cuckoo"
                hasResult={Boolean(cuckoo?.classification)}
                status={
                  analysisStatus?.cuckoo.display_status ||
                  analysisStatus?.cuckoo.additional_information
                }
              >
                <ThreatStatusLabel
                  className={styles.threat_status}
                  threatStatus={cuckoo?.classification?.toLowerCase()}
                  labelText={`${cuckoo?.score}`}
                />
              </IntegrationsValue>
            }
            onClick={event => {
              event.stopPropagation();
              if (!cuckoo?.classification) return;
              onClick(SECTIONS.DYNAMIC_ANALYSIS.CUCKOO);
            }}
            data-testid="report_summary_integrations_cuckoo"
          />
        </RenderWhen>
        <RenderWhen
          condition={Boolean(
            fireeye ||
              !getIsNotConfigured(
                analysisStatus?.fireeye.additional_information
              )
          )}
        >
          <KeyValueList.Item
            className={clsx(
              styles.list_item_integrations,
              styles.space_between,
              fireeye?.classification && styles.active
            )}
            keyClassName="font-normal"
            label={<IntegrationsKey icon="fireeye" title="FireEye" />}
            value={
              <IntegrationsValue
                integration="fireeye"
                hasResult={Boolean(fireeye?.classification)}
                status={
                  analysisStatus?.fireeye.display_status ||
                  analysisStatus?.fireeye.additional_information
                }
              >
                <ThreatStatusLabel
                  className={styles.threat_status}
                  threatStatus={fireeye?.classification?.toLowerCase()}
                />
              </IntegrationsValue>
            }
            onClick={event => {
              event.stopPropagation();
              if (!fireeye?.classification) return;
              onClick(SECTIONS.DYNAMIC_ANALYSIS.FIREEYE);
            }}
            data-testid="report_summary_integrations_fireeye"
          />
        </RenderWhen>
        <RenderWhen
          condition={Boolean(
            joe ||
              !getIsNotConfigured(analysisStatus?.joe.additional_information)
          )}
        >
          <KeyValueList.Item
            className={clsx(
              styles.list_item_integrations,
              styles.space_between,
              joe?.classification && styles.active
            )}
            keyClassName="font-normal"
            label={<IntegrationsKey icon="joe" title="Joe Sandbox" />}
            value={
              <IntegrationsValue
                integration="joe"
                hasResult={Boolean(joe?.classification)}
                status={
                  analysisStatus?.joe.display_status ||
                  analysisStatus?.joe.additional_information
                }
              >
                <ThreatStatusLabel
                  className={styles.threat_status}
                  threatStatus={joe?.classification?.toLowerCase()}
                />
              </IntegrationsValue>
            }
            onClick={event => {
              event.stopPropagation();
              if (!joe?.classification) return;
              onClick(SECTIONS.DYNAMIC_ANALYSIS.JOE);
            }}
            data-testid="report_summary_integrations_joe"
          />
        </RenderWhen>
        <RenderWhen
          condition={Boolean(
            vmray ||
              !getIsNotConfigured(
                analysisStatus?.vmray_tcbase.additional_information
              )
          )}
        >
          <KeyValueList.Item
            className={clsx(
              styles.list_item_integrations,
              styles.space_between,
              vmray?.classification && styles.active
            )}
            keyClassName="font-normal"
            label={<IntegrationsKey icon="vmray_tcbase" title="VMRay" />}
            value={
              <IntegrationsValue
                integration="vmray_tcbase"
                hasResult={Boolean(vmray?.classification)}
                status={
                  analysisStatus?.vmray_tcbase.display_status ||
                  analysisStatus?.vmray_tcbase.additional_information
                }
              >
                <ThreatStatusLabel
                  className={styles.threat_status}
                  threatStatus={vmray?.classification?.toLowerCase()}
                />
              </IntegrationsValue>
            }
            onClick={event => {
              event.stopPropagation();
              if (!vmray?.classification) return;
              onClick(SECTIONS.DYNAMIC_ANALYSIS.VMRAY);
            }}
            data-testid="report_summary_integrations_vmray"
          />
        </RenderWhen>
        <RenderWhen
          condition={Boolean(
            ciscoSMA ||
              !getIsNotConfigured(
                analysisStatus?.cisco_secure_malware_analytics
                  .additional_information
              )
          )}
        >
          <KeyValueList.Item
            className={clsx(
              styles.list_item_integrations,
              styles.space_between,
              ciscoSMA?.classification && styles.active
            )}
            keyClassName="font-normal"
            label={
              <IntegrationsKey
                icon="cisco_secure_malware_analytics"
                title="Cisco SMA"
              />
            }
            value={
              <IntegrationsValue
                integration="cisco_secure_malware_analytics"
                hasResult={Boolean(ciscoSMA?.classification)}
                status={
                  analysisStatus?.cisco_secure_malware_analytics
                    .display_status ||
                  analysisStatus?.cisco_secure_malware_analytics
                    .additional_information
                }
              >
                <ThreatStatusLabel
                  className={styles.threat_status}
                  threatStatus={ciscoSMA?.classification?.toLowerCase()}
                  labelText={`${ciscoSMA?.score}/100`}
                />
              </IntegrationsValue>
            }
            onClick={event => {
              event.stopPropagation();
              if (!ciscoSMA?.classification) return;
              onClick(SECTIONS.DYNAMIC_ANALYSIS.CISCO_SMA);
            }}
            data-testid="report_summary_integrations_cisco_sma"
          />
        </RenderWhen>
      </KeyValueList>
    </Card>
  );
};

export default Integrations;
